/** One standard report, run: dates and filters on one line over one table, with the two exports
 * beside them - ADR-0212 decision 6b.
 *
 * **What the page holds, and what it does not.** The columns, the rows, the totals and the notes are
 * the server's (`GET /data-reports/{key}`); this file formats a cell by its kind (`tco2e` is rounded
 * here, once, from the exact string; a blank is "no figure", never a zero) and sums nothing. The
 * search box filters the rows already read, in the browser, and says how many it shows.
 *
 * **Dates default to the top bar's period and may be changed here**, for this report only. A report
 * with no period and no dates does not run: the page does not invent a year.
 *
 * **A narrower view of the same report is a different report**, so the filters (site, vendor, scope)
 * go to the server and the table is re-read; the count printed is the rows the server returned.
 *
 * The server's notes (its basis and limits) are behind "About this report", in a drawer, so the page
 * stays a title, one line and one table.
 */

import { ChartBar, DownloadSimple, Info } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";

import { useMe } from "../../api/auth";
import { reportExportUrl, useDataReport } from "../../api/dataReports";
import type { CatalogueEntry, DataReport, ReportColumn, ReportRun } from "../../api/dataReports";
import { useFacilities } from "../../api/evidence";
import { useRegisters } from "../../api/sources";
import { Refused } from "../../api/refusal";
import { ReadFailed } from "../../components/Loaded";
import { EngagementWindowNote } from "../../components/EngagementWindowNote";
import { OutsideEngagement } from "../../components/OutsideEngagement";
import { Drawer } from "../../components/ui/Drawer";
import { EmptyState } from "../../components/ui/EmptyState";
import { FilterLine } from "../../components/ui/FilterLine";
import { EMPTY_FILTERS } from "../../components/ui/filters";
import type { FilterDef, FilterState } from "../../components/ui/filters";
import { GridTable } from "../../components/ui/GridTable";
import type { GridColumn } from "../../components/ui/GridTable";
import { TablePage } from "../../components/ui/TablePage";
import { isEngaged } from "../../lib/engagement";
import { quantity, shortDate, statedQuantity } from "../../lib/format";
import { keepSearch } from "../search";
import type { Screen } from "../useScreen";
import styles from "./DataReports.module.css";

/** A cell as the reader sees it. The rounding is here, once, from the server's exact string. */
export function showCell(kind: string, value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  switch (kind) {
    case "tco2e":
      return quantity(value, 2);
    case "number":
      return statedQuantity(value);
    case "date":
      return shortDate(value);
    default:
      return value;
  }
}

/** A row of the report, with the position it came in (the key: two rows may read alike). */
interface Line {
  id: string;
  cells: readonly (string | null)[];
}

function gridColumns(columns: readonly ReportColumn[]): GridColumn<Line>[] {
  return columns.map((column, at) => ({
    id: column.id,
    header: column.label,
    numeric: column.kind === "tco2e" || column.kind === "number",
    cell: (row) => showCell(column.kind, row.cells[at]),
  }));
}

/** The way back to the library. A link, so Back and a shared URL both work. */
function AllReportsLink() {
  return (
    <Link to="/data-reports" search={keepSearch} className={styles.back} data-testid="all-reports">
      All reports
    </Link>
  );
}

/** The report's own explanation, in a drawer: the server's notes, as it wrote them. */
function ReportNotes({ title, notes }: { title: string; notes: readonly string[] }) {
  const [open, setOpen] = useState(false);
  if (notes.length === 0) return null;
  return (
    <>
      <button type="button" className={styles.ghost} aria-haspopup="dialog" onClick={() => setOpen(true)}>
        <Info size={14} weight="bold" aria-hidden="true" />
        About this report
      </button>
      <Drawer open={open} onClose={() => setOpen(false)} title={title} width="sm">
        <div className={styles.notes}>
          {notes.map((note) => (
            <p key={note}>{note}</p>
          ))}
        </div>
      </Drawer>
    </>
  );
}

/** Two date inputs on the filter line. An empty or backwards pair is not sent: the previous valid
 * dates stay in force until a valid pair is typed. */
function DateRange({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) {
  const fromId = useId();
  const toId = useId();
  return (
    <div className={styles.dates} role="group" aria-label="Dates">
      <label htmlFor={fromId}>From</label>
      <input
        id={fromId}
        type="date"
        value={from}
        max={to || undefined}
        onChange={(event) => onChange(event.target.value, to)}
      />
      <label htmlFor={toId}>To</label>
      <input
        id={toId}
        type="date"
        value={to}
        min={from || undefined}
        onChange={(event) => onChange(from, event.target.value)}
      />
    </div>
  );
}

export function ReportRunView({ screen, entry }: { screen: Screen; entry: CatalogueEntry }) {
  const windowKey = screen.window ? `${screen.window.period_start}|${screen.window.period_end}` : "";
  const [dates, setDates] = useState({
    from: screen.window?.period_start ?? "",
    to: screen.window?.period_end ?? "",
  });
  // The top bar's period changed: the report follows it. Reset while rendering (React's pattern for
  // state that depends on a changed value), so the old dates never paint under a new period.
  const [seenWindow, setSeenWindow] = useState(windowKey);
  if (seenWindow !== windowKey) {
    setSeenWindow(windowKey);
    setDates({ from: screen.window?.period_start ?? "", to: screen.window?.period_end ?? "" });
  }
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  const needs = new Set(entry.needs);
  const facilities = useFacilities(needs.has("site"));
  const registers = useRegisters(needs.has("vendor"));

  const validDates = dates.from !== "" && dates.to !== "" && dates.from <= dates.to;
  const run: ReportRun | undefined =
    entry.available && validDates
      ? {
          key: entry.key,
          periodStart: dates.from,
          periodEnd: dates.to,
          method: screen.method,
          ...(filters.values.site?.[0] ? { facilityId: filters.values.site[0] } : {}),
          ...(filters.values.vendor?.[0] ? { vendorId: filters.values.vendor[0] } : {}),
          ...(filters.values.scope?.[0] ? { scope: filters.values.scope[0] } : {}),
        }
      : undefined;
  const report = useDataReport(run);
  const { data: me } = useMe();
  // **A report the engagement does not allow is not a report that failed to load** (Q-03, G-P7-16).
  // The audit trail is the chain, which cannot be narrowed to a period or a company (ADR-0048), so
  // an engaged Auditor is refused it **by design**, and a window outside the engagement is answered
  // 404 (the answer a window holding nothing also gets). Both were drawn as "This report could not be
  // loaded", which reads as a fault and invites a retry that cannot work.
  const withheld =
    isEngaged(me) &&
    report.error instanceof Refused &&
    (report.error.status === 403 ||
      (report.error.status === 404 &&
        ["outside_engagement", "no_such_site", "no_such_vendor"].includes(report.error.code)));

  const defs: FilterDef[] = [
    ...(needs.has("site")
      ? [
          {
            id: "site",
            label: "Site",
            mode: "single" as const,
            options: (facilities.data ?? []).map((site) => ({ value: site.id, label: site.name })),
          },
        ]
      : []),
    ...(needs.has("vendor")
      ? [
          {
            id: "vendor",
            label: "Vendor",
            mode: "single" as const,
            options: (registers.data?.vendors ?? []).map((vendor) => ({
              value: vendor.id,
              label: vendor.name,
            })),
          },
        ]
      : []),
    ...(needs.has("scope")
      ? [
          {
            id: "scope",
            label: "Scope",
            mode: "single" as const,
            options: ["1", "2", "3"].map((scope) => ({ value: scope, label: `Scope ${scope}` })),
          },
        ]
      : []),
  ];

  const needle = filters.search.trim().toLowerCase();
  const data: DataReport | undefined = report.data;
  const rows: Line[] = (data?.rows ?? [])
    .map((cells, at) => ({ id: String(at), cells }))
    .filter((row) => !needle || row.cells.some((cell) => cell?.toLowerCase().includes(needle)));

  const exports =
    run && data ? (
      <>
        <ReportNotes title={entry.title} notes={data.notes} />
        {(["csv", "xlsx"] as const).map((format) => (
          <a
            key={format}
            className={styles.export}
            href={reportExportUrl(run, format)}
            download
            data-testid={`export-${format}`}
          >
            <DownloadSimple size={14} weight="bold" aria-hidden="true" />
            {format === "csv" ? "Export CSV" : "Export workbook"}
          </a>
        ))}
      </>
    ) : null;

  const totals = data && Object.keys(data.totals).length > 0
    ? {
        label: "Total",
        values: Object.fromEntries(
          data.columns
            .filter((column) => column.id in data.totals)
            .map((column) => [column.id, showCell(column.kind, data.totals[column.id])]),
        ),
      }
    : undefined;

  return (
    <TablePage
      icon={ChartBar}
      title="Reports"
      label={entry.title}
      actions={<AllReportsLink />}
      filters={
        <FilterLine
          filters={defs}
          state={filters}
          onChange={setFilters}
          searchLabel="Search this report"
          savedViews={
            <DateRange
              from={dates.from}
              to={dates.to}
              onChange={(from, to) => setDates({ from, to })}
            />
          }
          resultCount={data ? { shown: rows.length, total: data.rows.length, noun: "rows" } : undefined}
          actions={exports}
        />
      }
      table={
        data ? (
          <GridTable
            caption={entry.title}
            columns={gridColumns(data.columns)}
            rows={rows}
            rowKey={(row) => row.id}
            totals={totals}
            empty="No figures in this period."
            testId="report-table"
          />
        ) : undefined
      }
      state={
        !entry.available ? (
          <EmptyState
            title="Not available yet"
            testId="report-unavailable"
            action={<ReportNotes title={entry.title} notes={entry.why_not ? [entry.why_not] : []} />}
          />
        ) : !validDates ? (
          <EmptyState title="Choose dates" testId="report-needs-dates" />
        ) : withheld ? (
          <EmptyState title="Not available for your engagement" testId="report-withheld">
            {report.error instanceof Refused && report.error.status === 403 ? (
              // The chain has no period or company to narrow to (ADR-0048); saying so is the whole
              // explanation, and the remedy is the one the Engagement page already offers.
              "The audit trail is the whole ledger, which cannot be narrowed to your engagement."
            ) : report.error instanceof Refused && report.error.code === "outside_engagement" ? (
              <EngagementWindowNote under={{ kind: "outside" }} />
            ) : (
              <OutsideEngagement
                noun={report.error instanceof Refused && report.error.code === "no_such_vendor" ? "vendor" : "site"}
                testId="report-outside-engagement"
              />
            )}
          </EmptyState>
        ) : (report.error ?? facilities.error ?? registers.error) ? (
          <ReadFailed
            of="This report"
            error={report.error ?? facilities.error ?? registers.error}
            onRetry={() => {
              void report.refetch();
              void facilities.refetch();
              void registers.refetch();
            }}
          />
        ) : data ? undefined : (
          <GridTable
            caption={entry.title}
            columns={[]}
            rows={[]}
            rowKey={(row: Line) => row.id}
            loading
            testId="report-loading"
          />
        )
      }
    />
  );
}
